import type { CancelReasonCode, FlightLogEntry, Mission, ReinstateActor } from "./types";
import { dualClockStamp } from "./clock";

export const CANCEL_REASON_CODES: readonly CancelReasonCode[] = [
  "operational",
  "scheduling",
  "conflict",
  "ferry_sandwich",
  "other",
];

export const CANCEL_REASON_LABEL: Record<CancelReasonCode, string> = {
  operational: "Operational reasons",
  scheduling: "Scheduling reasons",
  conflict: "Conflict",
  ferry_sandwich: "Redundant ferry pair (out-and-back)",
  other: "Other / custom",
};

export type CancelReasonOpts = {
  cancelReason?: string | null;
  cancelReasonNote?: string | null;
};

export type NormalizedCancelReason =
  | { ok: true; cancelReason: CancelReasonCode; cancelReasonNote?: string }
  | { ok: false; error: string };

export function isCancelReason(value: unknown): value is CancelReasonCode {
  return typeof value === "string" && (CANCEL_REASON_CODES as readonly string[]).includes(value);
}

/** Spoken / selected cancel reason → enum. Empty when the text is free-form or missing. */
export function parseCancelReason(raw: string): CancelReasonCode | "" {
  const key = raw.trim().toLowerCase().replace(/[_-]+/g, " ").replace(/\s+/g, " ");
  if (!key) return "";
  if (
    key === "operational" ||
    key === "ops" ||
    key === "operational reasons" ||
    key === "weather" ||
    key === "mx" ||
    key === "maintenance" ||
    key === "mechanical" ||
    key === "aircraft" ||
    key === "crew"
  ) {
    return "operational";
  }
  if (
    key === "scheduling" ||
    key === "schedule" ||
    key === "scheduling reasons" ||
    key === "timing" ||
    key === "slot" ||
    key === "delay"
  ) {
    return "scheduling";
  }
  if (key === "conflict" || key === "clash" || key === "overlap") return "conflict";
  if (
    key === "ferry sandwich" ||
    key === "sandwich" ||
    key === "redundant ferry" ||
    key === "redundant ferry pair" ||
    key === "out and back" ||
    key === "out-and-back" ||
    key === "unrealistic ferry" ||
    key === "useless ferry" ||
    key === "overnight ferry"
  ) {
    return "ferry_sandwich";
  }
  if (key === "other" || key === "custom" || key === "other / custom" || key === "other custom") {
    return "other";
  }
  if (isCancelReason(raw.trim())) return raw.trim() as CancelReasonCode;
  return "";
}

export function normalizeCancelReason(input: CancelReasonOpts = {}): NormalizedCancelReason {
  const note = typeof input.cancelReasonNote === "string" ? input.cancelReasonNote.trim() : "";
  const raw = typeof input.cancelReason === "string" ? input.cancelReason.trim() : "";
  if (!raw && !note) return { ok: false, error: "Reason required" };
  const parsed = parseCancelReason(raw);
  if (parsed === "other" && !note) return { ok: false, error: "Custom reason text required" };
  if (parsed) return { ok: true, cancelReason: parsed, cancelReasonNote: note || undefined };
  if (note && !raw) return { ok: true, cancelReason: "other", cancelReasonNote: note };
  if (raw) return { ok: true, cancelReason: "other", cancelReasonNote: raw };
  return { ok: false, error: "Reason required" };
}

export function formatCancelRemark(reason: CancelReasonCode, note?: string): string {
  const label = CANCEL_REASON_LABEL[reason];
  const extra = note?.trim();
  return extra ? `Cancelled — ${label}: ${extra}` : `Cancelled — ${label}`;
}

export function stampCancel(
  mission: Mission,
  input: {
    cancelReason: CancelReasonCode;
    cancelReasonNote?: string;
    at?: string;
    dispatchWarning?: string;
  },
): Mission {
  const at = input.at ?? new Date().toISOString();
  const remark = formatCancelRemark(input.cancelReason, input.cancelReasonNote);
  return {
    ...mission,
    status: "cancelled",
    lifecycle: "cancelled",
    cancelledAt: at,
    cancelReason: input.cancelReason,
    cancelReasonNote: input.cancelReasonNote,
    preCancelStatus: mission.status !== "cancelled" ? mission.status : mission.preCancelStatus,
    reinstatedAt: undefined,
    reinstatedBy: undefined,
    dispatchWarning: input.dispatchWarning ?? remark,
  };
}

export function formatReinstateRemark(actor: ReinstateActor, clashNote?: string): string {
  const who = actor === "megan" ? "Megan" : "desk";
  const extra = clashNote?.trim();
  return extra ? `Reinstated — ${who}. Clash: ${extra}` : `Reinstated — ${who}`;
}

export function stampReinstate(
  mission: Mission,
  input: { actor: ReinstateActor; at?: string; clashWarning?: string },
): Mission {
  const at = input.at ?? new Date().toISOString();
  const prior = mission.preCancelStatus && mission.preCancelStatus !== "cancelled" ? mission.preCancelStatus : "pending";
  const remark = formatReinstateRemark(input.actor, input.clashWarning);
  return {
    ...mission,
    status: prior,
    lifecycle: prior === "in-progress" ? "active" : "scheduled",
    cancelledAt: undefined,
    cancelReason: undefined,
    cancelReasonNote: undefined,
    preCancelStatus: undefined,
    reinstatedAt: at,
    reinstatedBy: input.actor,
    dispatchWarning: remark,
  };
}

export function stampCancelOnLog(
  log: FlightLogEntry,
  input: { cancelReason: CancelReasonCode; cancelReasonNote?: string; now?: Date },
): FlightLogEntry {
  const now = input.now ?? new Date();
  const stamp = dualClockStamp(now);
  const remark = formatCancelRemark(input.cancelReason, input.cancelReasonNote);
  const notes = log.notes?.includes(remark) ? log.notes : [log.notes, remark].filter(Boolean).join(" · ");
  return {
    ...log,
    status: "cancelled",
    cancelReason: input.cancelReason,
    cancelReasonNote: input.cancelReasonNote,
    notes,
    updatedAtUtc: stamp.utc,
    updatedAtLocal: stamp.local,
  };
}
